import { useRef, useState, useEffect } from "react";
import { Volume2 } from "lucide-react";

/** Compact speaker-output picker for the admin toolbar.
 *
 * Renders a small pill button showing the currently-selected output
 * device. Click to open a dropdown of available `audiooutput` devices.
 * Choosing a device calls `onSelect(deviceId)` and closes the dropdown.
 *
 * When device labels are anonymised (browser hasn't granted mic
 * permission), a subtle "Show speaker names" button asks for permission.
 */
export default function SpeakerPicker({
  devices,
  selectedDeviceId,
  onSelect,
  labelsLocked,
  onUnlockLabels,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    if (open) window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const currentDevice =
    devices.find((d) => d.deviceId === selectedDeviceId) ||
    devices.find((d) => d.deviceId === "default") ||
    null;
  // When the user has explicitly chosen "System default", show that
  // stable label — NOT whatever the browser names the underlying
  // physical default device (which varies by OS and confuses the owner).
  const currentLabel =
    !selectedDeviceId || selectedDeviceId === "default"
      ? "System default"
      : (currentDevice?.label && currentDevice.label.trim()) || "Custom speaker";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        data-testid="admin-speaker-picker"
        onClick={() => setOpen((o) => !o)}
        title="Choose which speaker plays the new-order ding"
        className="inline-flex items-center gap-2 bg-white border border-[#E7E5E4] hover:border-[#0E0E1A] text-[#0E0E1A] rounded-full px-4 py-2.5 font-semibold text-sm max-w-[220px]"
      >
        <Volume2 className="w-4 h-4 shrink-0" />
        <span
          className="truncate"
          data-testid="admin-speaker-picker-current"
        >
          {currentLabel}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 z-30 w-72 rounded-xl border border-[#E7E5E4] bg-white shadow-xl overflow-hidden"
          data-testid="admin-speaker-picker-menu"
        >
          <div className="px-4 py-2 text-xs uppercase tracking-wider text-[#57534E] border-b border-[#E7E5E4]">
            Speaker output
          </div>
          <ul className="max-h-64 overflow-y-auto">
            {devices.length === 0 && (
              <li className="px-4 py-3 text-sm text-[#57534E]">
                No output devices detected.
              </li>
            )}
            {devices.map((d) => {
              const isSelected = d.deviceId === selectedDeviceId;
              const label =
                (d.label && d.label.trim()) ||
                (d.deviceId === "default"
                  ? "System default"
                  : d.deviceId === "communications"
                    ? "Communications default"
                    : `Speaker ${d.deviceId.slice(0, 6)}…`);
              return (
                <li key={d.deviceId}>
                  <button
                    type="button"
                    data-testid={`admin-speaker-option-${d.deviceId}`}
                    onClick={() => {
                      onSelect(d.deviceId);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-[#F5F5F4] transition-colors ${
                      isSelected ? "bg-[#C9853E]/10 text-[#A6692D] font-semibold" : "text-[#0E0E1A]"
                    }`}
                  >
                    <span className="block truncate">{label}</span>
                    {isSelected && (
                      <span className="text-xs text-[#A6692D]">Currently routing here</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {labelsLocked && (
            <button
              type="button"
              data-testid="admin-speaker-unlock-labels"
              onClick={() => {
                onUnlockLabels();
                setOpen(false);
              }}
              className="w-full px-4 py-3 text-xs text-[#57534E] border-t border-[#E7E5E4] hover:bg-[#F5F5F4] text-left"
            >
              Show real speaker names&nbsp;
              <span className="opacity-70">
                (browser will ask for microphone permission — needed only to reveal device labels; we don&apos;t record anything)
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
